import {
	Opportunity,
	OpportunityRequest,
	OpportunityResponse,
	AddOpportunityRequest,
	EditOpportunityRequest,
	KanbanOpportunityResponse,
	KanbanOpportunityCountResponse,
	GetSyncErrorsInterface,
	allSyncErrors,
	PendingOpportunitySharedWithPartnerResponse,
	PendingOpportunitySharedWithPartner,
	AcceptPendingOpportunitySharedWithPartnerRequest,
	RejectPendingOpportunitySharedWithPartnerRequest,
	RecentOpportunityRequest,
	RecentOpportunityResponse
} from "@/Contracts/Opportunity";

import axios, { AxiosResponse } from "axios";

import {
	postRequest,
	getRequest,
	deleteRequest,
	putRequest
} from "@/Utils/Service/BackendService";

import {
	getTableQueryFilters,
	includeChildEntities,
	ChildEntityHydrationParams
} from "@/Contracts/Utils";
import {
	buildSearchableQueryString,
	OPPORTUNITY_ATLAS_SAFE_FIELDS
} from "@/Services/_shared/buildSearchableQueryUrl";
import { ClientsResponse, DeletedRecord, ZomentumEntities } from "@/Contracts";

import {
	CustomViewsMetadata,
	CustomViewResponse,
	ListPagePostRequest,
	CustomView
} from "@/Contracts/UI";

import { DynamicFormCreateRequest } from "@/Contracts/Documents";
import { ShareOpportunityWithVendorRequest } from "@/Contracts/Opportunity";
import { getEntityCurrencyCode } from "@/Utils/Currency";

const getOpportunitiesUrl = "/v2/opportunities";
const getOpportunityDetailsUrl = "/v2/opportunities/{opportunity_id}";
const deleteOpportunityUrl = "/v2/opportunities/{opportunity_id}";
const editOpportunityUrl = "/v2/opportunities/{opportunity_id}";
const mergeOpportunityUrl =
	"/v2/opportunities/{opportunity_id}/merge-with/{merge_opportunity_id}";
const getAllOpportunitiesKanbanUrl = "/v2/opportunities/kanban";
const getAllOpportunitiesCountForKanbanUrl = "/v2/opportunities/count";

const getAllOpportunityViewURL = "/v2/views?entity_type={entity_type}";
const getOpportunityViewMetadataURL =
	"/v2/views/metadata?entity_type={entity_type}";

const getOpportunityListPostURL = "/v2/opportunities/view";
const createOpportunityViewURL = "/v2/views";
const updateOpportunityViewURL = "/v2/views/{view_id}";
const deleteOpportunityViewURL = "/v2/views/{view_id}";

const getOpportunityKanbanPostURL = "/v2/opportunities/view/kanban";
const getOpportunityCountKanbanPostURL = "/v2/opportunities/view/count";

const postPushChangesToConnectwise = "/v2/opportunities/export-to-psa/:id";
const getSyncErrorsURL = "/v2/integration/get_sync_errors";

const shareOpportunityWithVendorURL = "/v2/opportunities/:id/share-with-vendor";

const recentOpportunityURL = "/v2/opportunities/recent/";

const getPendingOpportunitiesShareWithPartnerURL = "/v2/opportunities/pending";
const getPendingOpportunityShareWithPartnerURL =
	"/v2/opportunities/pending/:opportunityId";
const checkConflictForPendingOpportunityShareWithPartnerURL =
	"/v2/opportunities/pending/:opportunityId/check-conflict";
const acceptPendingOpportunitySharedWithPartnerURL =
	"/v2/opportunities/pending/:opportunityId/accept";
const rejectPendingOpportunitySharedWithPartnerURL =
	"/v2/opportunities/pending/:opportunityId/reject";

export const getOpportunities = async (
	opportunityRequest: OpportunityRequest,
	pipelineId?: string
): Promise<OpportunityResponse> => {
	try {
		let queryParams = `limit=${opportunityRequest.limit}`;
		const included_child_entities = includeChildEntities(
			opportunityRequest.included_child_entities
		);
		queryParams = `${queryParams}${
			included_child_entities && included_child_entities.length > 0
				? `&${included_child_entities}`
				: ""
		}`;
		const searchableSegment = buildSearchableQueryString({
			filters: opportunityRequest.filters,
			keywords: opportunityRequest.keywords,
			safeFields: OPPORTUNITY_ATLAS_SAFE_FIELDS,
			modelLabel: "Opportunity"
		});
		if (searchableSegment.length > 0) {
			queryParams = `${queryParams}&${searchableSegment}`;
		}

		if (opportunityRequest.selected_id) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!opportunityRequest?.selected_id
					? `selected_id=${opportunityRequest.selected_id}`
					: ""
			}`;
		}
		if (opportunityRequest.use_all_pipelines_flag) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!opportunityRequest?.use_all_pipelines_flag
					? `use_all_pipelines_flag=${true}`
					: ""
			}`;
		}

		const response: AxiosResponse<OpportunityResponse> = await axios(
			getRequest(
				`${getOpportunitiesUrl}?${queryParams}${
					pipelineId
						? `&optional_pipeline_id_field=${pipelineId}`
						: ""
				}`,
				null,
				true,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getAllOpportunitiesForKanban = async (
	opportunityRequest: OpportunityRequest,
	pipelineId?: string
): Promise<KanbanOpportunityResponse> => {
	try {
		const params =
			opportunityRequest.filters.length > 0
				? `?${getTableQueryFilters(opportunityRequest.filters, false)}&`
				: "?";

		const includeChildEntitiesParams = includeChildEntities(
			opportunityRequest.included_child_entities
		);
		const url =
			getAllOpportunitiesKanbanUrl +
			params +
			includeChildEntitiesParams +
			(pipelineId ? `&optional_pipeline_id_field=${pipelineId}` : "");
		const response: AxiosResponse<KanbanOpportunityResponse> = await axios(
			getRequest(url, null, true, true, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
export const getAllOpportunitiesCountForKanban = async (
	opportunityRequest: OpportunityRequest,
	pipelineId?: string
): Promise<KanbanOpportunityCountResponse[]> => {
	try {
		const params =
			opportunityRequest.filters.length > 0
				? `?${getTableQueryFilters(opportunityRequest.filters, false)}&`
				: "?";

		const includeChildEntitiesParams = includeChildEntities(
			opportunityRequest.included_child_entities
		);
		const url =
			getAllOpportunitiesCountForKanbanUrl +
			params +
			includeChildEntitiesParams +
			(pipelineId ? `&optional_pipeline_id_field=${pipelineId}` : "");
		const response: AxiosResponse<KanbanOpportunityCountResponse[]> =
			await axios(getRequest(url, null, true, true, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getOpportunityDetails = async (
	opportunityId: string,
	includeBlocks = false
): Promise<Opportunity> => {
	try {
		const includedChildEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.CLIENT_COMPANIES_NAME,
				ChildEntityHydrationParams.DOCUMENTS_ALL,
				ChildEntityHydrationParams.OPPORTUNITY_ACTION_STATUS,
				ChildEntityHydrationParams.CLIENT_COMPANY_SYNC_DATA,
				ChildEntityHydrationParams.VENDORS_ALL,
				ChildEntityHydrationParams.PARTNER_TIERS_ALL,
				ChildEntityHydrationParams.VENDOR_OPPORTUNITIES_INSIGHTS,
				...(includeBlocks
					? [ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION]
					: [])
			)
		);
		const url =
			getOpportunityDetailsUrl.replace(
				"{opportunity_id}",
				opportunityId
			) +
			(includedChildEntities && includedChildEntities.length > 0
				? "?" + includedChildEntities
				: "");
		const response: AxiosResponse<Opportunity> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getOpportunitySummary = async (
	opportunityId: string
): Promise<Opportunity> => {
	try {
		const url = getOpportunityDetailsUrl.replace(
			"{opportunity_id}",
			opportunityId
		);
		const response: AxiosResponse<Opportunity> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// Backend's CreateOpportunity/EditOpportunity requests want `currency` as a
// bare code string, but Redux/GET represent it as a Currency object.
// Normalize at the wire boundary so callers don't have to.
const normalizeCurrencyForRequest = <T extends { currency?: unknown }>(
	payload: T
): T => {
	if (
		payload.currency &&
		typeof payload.currency === "object" &&
		"code" in payload.currency
	) {
		return { ...payload, currency: getEntityCurrencyCode(payload) };
	}
	return payload;
};

export const addOpportunity = async (
	addOpportunityRequest: AddOpportunityRequest
): Promise<Opportunity> => {
	const includedChildEntities = includeChildEntities(
		addOpportunityRequest.included_child_entities
	);
	const url =
		getOpportunitiesUrl +
		(includedChildEntities && includedChildEntities.length > 0
			? "?" + includedChildEntities
			: "");
	try {
		const response: AxiosResponse<Opportunity> = await axios(
			postRequest(
				url,
				normalizeCurrencyForRequest(addOpportunityRequest),
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteOpportunity = async (
	opportunity: Opportunity
): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(
				deleteOpportunityUrl.replace(
					"{opportunity_id}",
					opportunity.id
				),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editOpportunity = async (
	editOpportunityRequest: Partial<EditOpportunityRequest> &
		Pick<EditOpportunityRequest, "id">
): Promise<Opportunity> => {
	const includedChildEntities = includeChildEntities(
		editOpportunityRequest.included_child_entities
	);
	const url =
		editOpportunityUrl.replace(
			"{opportunity_id}",
			editOpportunityRequest.id
		) +
		(includedChildEntities && includedChildEntities.length > 0
			? "?" + includedChildEntities
			: "");
	try {
		const response: AxiosResponse<Opportunity> = await axios(
			putRequest(
				url,
				normalizeCurrencyForRequest(editOpportunityRequest),
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getOpportunityViewMetadata =
	async (): Promise<CustomViewsMetadata> => {
		try {
			const response: AxiosResponse<CustomViewsMetadata> = await axios(
				getRequest(
					getOpportunityViewMetadataURL.replace(
						"{entity_type}",
						ZomentumEntities.Opportunity
					),
					null,
					true,
					false,
					false
				)
			);
			return response.data;
		} catch (error) {
			console.error(error);
			return Promise.reject(error);
		}
	};

export const getAllOpportunityViews = async (): Promise<CustomViewResponse> => {
	try {
		const response: AxiosResponse<CustomViewResponse> = await axios(
			getRequest(
				getAllOpportunityViewURL.replace(
					"{entity_type}",
					ZomentumEntities.Opportunity
				),
				null,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getOpportunityListPost = async (
	opportunityRequest: ListPagePostRequest
): Promise<OpportunityResponse> => {
	try {
		const response: AxiosResponse<OpportunityResponse> = await axios(
			postRequest(
				getOpportunityListPostURL,
				opportunityRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const createOpportunityView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			postRequest(
				createOpportunityViewURL,
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const updateOpportunityView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			putRequest(
				updateOpportunityViewURL.replace(
					"{view_id}",
					customViewRequest.id
				),
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const deleteOpportunityView = async (
	customViewId: string
): Promise<unknown> => {
	try {
		const response: AxiosResponse<unknown> = await axios(
			deleteRequest(
				deleteOpportunityViewURL.replace("{view_id}", customViewId),
				null,
				true,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getOpportunitiesForKanbanPost = async (
	opportunityRequest: ListPagePostRequest
): Promise<KanbanOpportunityResponse> => {
	try {
		const response: AxiosResponse<KanbanOpportunityResponse> = await axios(
			postRequest(
				getOpportunityKanbanPostURL,
				opportunityRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};
export const getOpportunitiesCountForKanbanPost = async (
	opportunityRequest: ListPagePostRequest
): Promise<KanbanOpportunityCountResponse[]> => {
	try {
		const response: AxiosResponse<KanbanOpportunityCountResponse[]> =
			await axios(
				postRequest(
					getOpportunityCountKanbanPostURL,
					opportunityRequest,
					true,
					false,
					false
				)
			);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getOpportunityDocuments = async (
	opportunityId: string
): Promise<Opportunity> => {
	try {
		const includedChildEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.DOCUMENTS_ALL
			)
		);
		const url =
			getOpportunityDetailsUrl.replace(
				"{opportunity_id}",
				opportunityId
			) +
			(includedChildEntities && includedChildEntities.length > 0
				? "?" + includedChildEntities
				: "");
		const response: AxiosResponse<Opportunity> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const pushChangesToConnectwise = async (
	documentId: string,
	pushChangesToConnectwiseRequest: DynamicFormCreateRequest
): Promise<void> => {
	try {
		await axios(
			putRequest(
				postPushChangesToConnectwise.replace(":id", documentId),
				pushChangesToConnectwiseRequest,
				true,
				false,
				false
			)
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getSyncErrors = async (
	errorRequest: GetSyncErrorsInterface
): Promise<allSyncErrors> => {
	try {
		const response = await axios(
			postRequest(getSyncErrorsURL, errorRequest, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const shareOpportunityWithVendor = async (
	opportunityId: string,
	shareOpportunityWithVendorRequest: ShareOpportunityWithVendorRequest,
	includedChildEntities: ChildEntityHydrationParams[]
): Promise<Opportunity> => {
	try {
		const url = `${shareOpportunityWithVendorURL.replace(
			":id",
			opportunityId
		)}?${includeChildEntities(includedChildEntities)}`;
		const response: AxiosResponse<Opportunity> = await axios(
			putRequest(
				url,
				shareOpportunityWithVendorRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getPendingOpportunitiesSharedWithPartnerRequest =
	async (): Promise<PendingOpportunitySharedWithPartnerResponse> => {
		try {
			const includedChildEntities = includeChildEntities([
				ChildEntityHydrationParams.VENDORS_ALL
			]);
			const response: AxiosResponse<PendingOpportunitySharedWithPartnerResponse> =
				await axios(
					getRequest(
						getPendingOpportunitiesShareWithPartnerURL +
							`?${includedChildEntities}`,
						null,
						true,
						true,
						false
					)
				);
			return response.data;
		} catch (error) {
			return Promise.reject(error);
		}
	};

export const getPendingOpportunitySharedWithPartnerRequest = async (
	opportunityId: string
): Promise<PendingOpportunitySharedWithPartner> => {
	try {
		const includedChildEntities = includeChildEntities([
			ChildEntityHydrationParams.VENDORS_ALL,
			ChildEntityHydrationParams.PARTNER_TIERS_ALL,
			ChildEntityHydrationParams.SHARED_CLIENTS_ALL,
			ChildEntityHydrationParams.OPPORTUNITIES_IS_DUPLICATE
		]);
		const response: AxiosResponse<PendingOpportunitySharedWithPartner> =
			await axios(
				getRequest(
					getPendingOpportunityShareWithPartnerURL.replace(
						":opportunityId",
						opportunityId
					) + `?${includedChildEntities}`,
					null,
					true,
					true,
					false
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const checkConflictForPendingOpportunityShareWithPartner = async (
	opportunityId: string
): Promise<ClientsResponse> => {
	try {
		const response: AxiosResponse<ClientsResponse> = await axios(
			getRequest(
				checkConflictForPendingOpportunityShareWithPartnerURL.replace(
					":opportunityId",
					opportunityId
				),
				null,
				true,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const acceptPendingOpportunitySharedWithPartner = async (
	opportunityId: string,
	clientId?: string,
	includedChildEntities?: ChildEntityHydrationParams[]
): Promise<Opportunity> => {
	try {
		const acceptPendingOpportunitySharedWithPartnerRequest: AcceptPendingOpportunitySharedWithPartnerRequest =
			{
				client_company_id: clientId
			};
		const response: AxiosResponse<Opportunity> = await axios(
			postRequest(
				`${acceptPendingOpportunitySharedWithPartnerURL.replace(
					":opportunityId",
					opportunityId
				)}?${includeChildEntities(includedChildEntities ?? [])}`,
				acceptPendingOpportunitySharedWithPartnerRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const rejectPendingOpportunitySharedWithPartner = async (
	opportunityId: string,
	rejectReason: string
): Promise<Opportunity> => {
	try {
		const rejectPendingOpportunitySharedWithPartnerRequest: RejectPendingOpportunitySharedWithPartnerRequest =
			{
				reject_reason: rejectReason
			};
		const response: AxiosResponse<Opportunity> = await axios(
			postRequest(
				rejectPendingOpportunitySharedWithPartnerURL.replace(
					":opportunityId",
					opportunityId
				),
				rejectPendingOpportunitySharedWithPartnerRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const mergeOpportunity = async (
	opportunityId: string,
	mergeOpportunityId: string
): Promise<Opportunity> => {
	try {
		const response: AxiosResponse<Opportunity> = await axios(
			putRequest(
				mergeOpportunityUrl
					.replace("{opportunity_id}", opportunityId)
					.replace("{merge_opportunity_id}", mergeOpportunityId),
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getRecentOpportunities = async (
	request: RecentOpportunityRequest
): Promise<RecentOpportunityResponse> => {
	const hasIds =
		(request.client_company_ids?.length ?? 0) > 0 ||
		(request.client_user_ids?.length ?? 0) > 0;
	if (!hasIds) {
		return { data: [], filtered_count_map: null };
	}
	try {
		const response: AxiosResponse<RecentOpportunityResponse> = await axios(
			postRequest(recentOpportunityURL, request, true, false, false)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getRecentOpportunitiesByClientCompanyIds = (
	clientCompanyIds: string[]
): Promise<RecentOpportunityResponse> =>
	getRecentOpportunities({ client_company_ids: clientCompanyIds });

export const getRecentOpportunitiesByClientUserIds = (
	clientUserIds: string[]
): Promise<RecentOpportunityResponse> =>
	getRecentOpportunities({ client_user_ids: clientUserIds });
